/* eslint-disable no-restricted-globals */
import React, { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  createStyles,
  makeStyles,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Theme,
  Paper,
  TableBody,
  Link,
} from '@material-ui/core';
import { Typography } from '@cbreone/core-ui';
import { BHPropertyFieldRule } from '../../types/bh-property-field-rule';
import { AVAILABILITY_NAME_KEY, linkAttributes, linkAttributes_pac, linkAttributes_UK_Regional, Measurement } from '../../data/constants';
import { Survey } from '../../types';
import { getColumnHeader } from '../../utilities';
import { getAvailabilityNameHeader, isAvailabilityNameApplicable, getAvailabilityFieldValues } from '../../utilities/property/propertyUtil';
import { calculateSubEntityTotals, extractUnit, formatNumericValue } from '../../utilities/property/tableCalculations';
import { CountriesWithCE, CountriesWithPAC, CountryKeys, CountriesWithTotals } from '../../map/constants/MapStyles';
import DOMPurify from 'dompurify';

export type Props = {
  survey: Survey;
  displayFields: BHPropertyFieldRule[];
  availabilities: any[];
  isCESurvey: boolean;
  templateData?: any; // Template configuration for nested tables
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      fontSize: '16px',
      fontWeight: 400,
      wordWrap: 'break-word',
      '&:nth-of-type(even)': {
        backgroundColor: '#fbfcfc',

        '& .MuiTableCell-root:first-of-type': {
          background: '#e6eaea',
        },
      },
      '&:nth-of-type(odd)': {
        '& .MuiTableCell-root:first-of-type': {
          background: '#F5F7F7',
        },
        background: theme.palette.common.white,
      },
      verticalAlign: 'top',
    },
    availabilitiesText: {
      color: theme.palette.primary.main,
    },
    paperRoot: {
      boxShadow: 'none',
      marginTop: '16px',
    },
    stickyColumn: {
      position: 'sticky',
      zIndex: 1000,
      left: '0px',
      background: '#e6eaea',
    },
    tableHeader: {
      whiteSpace: 'pre-wrap',
      background: '#F5F7F7',
      fontSize: '16px',
      fontWeight: 500,
      '&>.MuiTableCell-root': {
        borderBottom: '1px solid #DFE6E5',
      },
    },
    noBottomBorder: {
      zIndex: 2,
      borderBottom: 'none',
      color: theme.palette.text.primary,
      whiteSpace: 'pre-line',
      width: '134px',
      maxWidth: '134px',
      padding: '4px',
      boxSizing: 'content-box',
      wordBreak: 'break-word',
      minWidth: '134px',
      verticalAlign: 'top',
      '&.MuiTableCell-root': {
        fontSize: '16px',
      },
      '&:first-of-type': {
        width: '188px',
        maxWidth: '188px',
        minWidth: '188px',
        zIndex: 100,
      },

      '@media(max-width:768px)': {
        width: '86px',
        maxWidth: '86px',
        minWidth: '86px',

        '&:first-of-type': {
          width: '188px',
          maxWidth: '188px',
          minWidth: '188px',
        },
      },
    },
    contentStyle: {
      '& p': {
        marginTop: '0.5em',
        marginBottom: '0.5em',
      },
      '& p:first-child': {
        marginTop: '0em',
      },
      '& p:has(> br:only-child)': {
        marginTop: '-0.5em',
        marginBottom: '-0.5em',
      },
    },
    link: {
      wordWrap: 'break-word',
      fontSize: '16px',
    },
  }),
);

const PropertyAvailabilities: React.FC<Props> = ({
  survey,
  displayFields,
  availabilities,
  isCESurvey,
  templateData,
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const surveyCountry_PAC = CountriesWithPAC.includes(survey.surveyCountry);
  const surveyCountry_CE: boolean = CountriesWithCE.includes(survey.surveyCountry);
  const surveyCountry_UK: boolean = survey.surveyCountry === CountryKeys.UK;
  const containerRef: any = useRef();
  const displayAvailabilities =
    availabilities
      .filter((availability) => availability.status === 'ACTIVE')
      .sort((first: any, second: any) => first.order - second.order) || [];
  const availabilityNameHeader = useMemo(
    () => getAvailabilityNameHeader(survey?.surveyCountry), [survey?.surveyCountry]);

  const isIncludeAvailabilityName = useMemo(
    () => isAvailabilityNameApplicable(survey?.surveyCountry, `${survey?.propertyType}`),
    [survey?.surveyCountry, survey?.propertyType]);

  // Check if survey country should show totals (NL/FIN/AT markets)
  const showTotals = useMemo(() => {
    const surveyCountry = survey?.surveyCountry;
    return CountriesWithTotals.includes(surveyCountry);
  }, [survey?.surveyCountry]);

  function getAvailabilityNameValueLocal(availability: any) {
    const name = availability[AVAILABILITY_NAME_KEY]?.replace(/, /g, ',\r\n') || '-';
    return name;
  }

  // Helper function to get sub-entity (nested table) data for an availability
  const getSubEntityData = (availability: any, parentFields: any[]) => {
    // If templateData is provided, use it (survey-lib style)
    if (templateData?.sections && availability?.data) {
      const subEntityResults: any[] = [];

      // Find sections with sectionType: "table" (these are sub-entities)
      const tableSections = templateData.sections.filter((section: any) => section.sectionType === 'table');

      tableSections.forEach((section: any) => {
        const subEntityKey = section.sectionKey;
        const subEntityData = availability.data[subEntityKey];

        if (subEntityData && Array.isArray(subEntityData)) {
          // Get fields for this sub-entity from template
          const subEntityFields = section.columns || [];

          // Create a Set of field keys that belong to the sub-entity
          const subEntityFieldKeys = new Set(
            subEntityFields
              .filter((f: any) => f.status === 'ACTIVE')
              .map((f: any) => f.fieldKey || f.field),
          );

          const filteredData = subEntityData
            .filter((item: any) => !item.status || item.status === 'ACTIVE')
            .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

          subEntityResults.push({
            entityName: section.sectionName || section.sectionKey,
            subEntityFields: subEntityFields
              .filter((f: any) => f.status === 'ACTIVE')
              .sort((a: any, b: any) => a.order - b.order),
            parentFields, // Include all parent availability fields
            data: filteredData,
            availability, // Pass parent availability for merged cell values
            subEntityFieldKeys, // Set of field keys that belong to sub-entity
            allColumns: subEntityFields, // Keep unfiltered columns for checking field status
          });
        }
      });

      return subEntityResults.length > 0 ? subEntityResults : null;
    }

    // Fallback: Auto-detect nested array data in availability.data
    if (availability?.data) {
      const subEntityResults: any[] = [];
      const parentFieldKeys = new Set(parentFields.map((f) => f.fieldKey));

      // Look for array properties in availability.data
      Object.keys(availability.data).forEach((key) => {
        const value = availability.data[key];

        // Check if this is an array and not a parent field
        if (Array.isArray(value) && value.length > 0 && !parentFieldKeys.has(key)) {
          // Get field keys from the first item in the array
          const firstItem = value[0];
          if (firstItem && typeof firstItem === 'object') {
            const subEntityFieldKeys = new Set(Object.keys(firstItem).filter((k) => k !== 'status' && k !== 'order'));

            // Create field definitions from the data
            const subEntityFields = Array.from(subEntityFieldKeys).map((fieldKey, index) => ({
              field: fieldKey,
              fieldLabel: fieldKey.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
              order: index,
              status: 'ACTIVE',
            }));

            const filteredData = value
              .filter((item: any) => !item.status || item.status === 'ACTIVE')
              .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

            subEntityResults.push({
              entityName: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
              subEntityFields,
              parentFields,
              data: filteredData,
              availability,
              subEntityFieldKeys,
            });
          }
        }
      });

      return subEntityResults.length > 0 ? subEntityResults : null;
    }

    return null;
  };

  useEffect(() => {
    if (containerRef?.current) {
      containerRef?.current.scrollTo(0, 0);
    }
  }, [displayAvailabilities]);

  const emptyCells = (keyPrefix: string) => {
    const width = window.innerWidth;
    let numEmptyCol = 6 - displayFields.length;
    if (width <= 768) {
      numEmptyCol = Math.floor((width - 188) / 94) - displayFields.length;
    } else if (width <= 1024) {
      const value = Math.floor((width - 188) / 142);

      numEmptyCol = (value > 6 ? 6 : value) - displayFields.length;
    }

    return numEmptyCol > 0
      ? [...Array(numEmptyCol)].map((item, i) => (
        <TableCell
          key={`${keyPrefix}-empty-${i}`}
          component="th"
          classes={{ root: classes.noBottomBorder }}
          className="empty-cell"
        />
      ))
      : null;
  };
  const renderLinks = (value: string) => {
    // Split by comma first, then process each part
    const parts = value.split(',').map((part) => part.trim()).filter((part) => part.length > 0);

    return parts.map((item: string, idx: number) => {
      // Match label - URL pattern, allowing for various URL formats
      const labelUrlMatch = item.match(/^(.*?)\s*-\s*((?:https?:\/\/)?[\w.-]+(?:\.[\w]+)+(?:\/[^\s]*)?)$/);

      if (labelUrlMatch) {
        const label = labelUrlMatch[1]?.trim() || '';
        let url = labelUrlMatch[2]?.trim() || '';
        // Add protocol if missing
        if (url && !url.startsWith('http')) {
          url = `https://${url}`;
        }

        return (
          <React.Fragment key={`link-${idx}`}>
            {label ? `${label} - ` : ''}
            {url && (
              <Link
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}
              >
                {labelUrlMatch[2]} {/* Show original URL format */}
              </Link>
            )}
            {idx < parts.length - 1 && <br />} {/* Add break except for last item */}
          </React.Fragment>
        );
      }

      // Check if the item contains both text and URL (like "Test 1 www.google.com")
      const textUrlMatch = item.match(/^(.*?)\s+((?:https?:\/\/)?[\w.-]+(?:\.[\w]+)+(?:\/[^\s]*)?)$/);
      if (textUrlMatch) {
        const text = textUrlMatch[1]?.trim() || '';
        let url = textUrlMatch[2]?.trim() || '';
        // Add protocol if missing
        if (url && !url.startsWith('http')) {
          url = `https://${url}`;
        }

        return (
          <React.Fragment key={`text-url-${idx}`}>
            {text}{' '}
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              {textUrlMatch[2]} {/* Show original URL format */}
            </Link>
            {idx < parts.length - 1 && <br />}
          </React.Fragment>
        );
      }

      // Check if the item is just a URL (like www.google.com)
      const urlMatch = item.match(/^((?:https?:\/\/)?[\w.-]+(?:\.[\w]+)+(?:\/[^\s]*)?)$/);
      if (urlMatch) {
        let url = urlMatch[1]?.trim() || '';
        // Add protocol if missing
        if (url && !url.startsWith('http')) {
          url = `https://${url}`;
        }

        return (
          <React.Fragment key={`url-${idx}`}>
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              {item} {/* Show original URL format */}
            </Link>
            {idx < parts.length - 1 && <br />}
          </React.Fragment>
        );
      }

      // Fallback for items that don't match any pattern
      return (
        <React.Fragment key={`text-${idx}`}>
          {item}
          {idx < parts.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  /**
   * Formats a measurement text string by splitting it into lines,
   * bolding specific area types, and rendering each line as a Typography element.
   * Returns a dash if the value is empty or '-'.
   * @param value - The measurement text to format and render.
   */
  const renderFormattedMeasurementText = (value: string) => {
    if (!value || value === '-') {
      return <Typography variant="body1">-</Typography>;
    }

    // Split by newlines to process each line
    const lines = value.split('\n');
    // eslint-disable-next-line no-undef
    const formattedLines: JSX.Element[] = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      // Define area types that should be bold
      const areaTypes = [
        'Mezzanine Area',
        'Warehouse Area',
        'Office Area',
      ];

      // Check if line contains any area type that should be bold
      const matchingAreaType = areaTypes.find((areaType) =>
        trimmedLine.toLowerCase().includes(areaType.toLowerCase()),
      );

      if (matchingAreaType) {
      // Find the exact match (case-insensitive) and replace with bold version
        const regex = new RegExp(`(${matchingAreaType})`, 'gi');
        const parts = trimmedLine.split(regex);

        formattedLines.push(
        <div key={`line-${index}`}>
          <Typography variant="body1">
            {parts.map((part, partIndex) => {
              if (part.toLowerCase() === matchingAreaType.toLowerCase()) {
                return <strong key={partIndex}>{part}</strong>;
              }
              return part;
            })}
          </Typography>
        </div>,
        );
      } else {
        formattedLines.push(
        <Typography key={`line-${index}`} variant="body1">
          {trimmedLine}
        </Typography>,
        );
      }
    });

    return <div>{formattedLines}</div>;
  };
  return displayAvailabilities?.length > 0 ? (
    <>
      <Typography variant="h6" className={classes.availabilitiesText}>
        {t('availabilities.title')}
      </Typography>

      <Paper className={classes.paperRoot}>
        <TableContainer ref={containerRef} style={{ width: '100%' }}>
          <Table
            stickyHeader
            aria-label="sticky table"
            style={{ tableLayout: 'fixed' }}
          >
            <TableHead>
              <TableRow className={classes.tableHeader}>
                {
                  isIncludeAvailabilityName && <TableCell
                    component="th"
                    key={'availabilityName'}
                    className={`${classes.noBottomBorder} ${classes.stickyColumn}`}
                  >
                    <Typography variant="body1"> {availabilityNameHeader} </Typography>
                  </TableCell>
                }
                {displayFields.map((column: any) => (
                  <TableCell
                    component="th"
                    key={column.fieldKey}
                    className={classes.noBottomBorder}
                  >
                   <Typography variant="body1">{getColumnHeader(t, i18n.language, column.fieldKey, column.fieldLabel)}</Typography>
                  </TableCell>
                ))}
                {emptyCells('table-header')}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayAvailabilities.map((row: any, index) => {
                if (!row) {
                  return null;
                }

                const subEntityData = getSubEntityData(row, displayFields);

                // Collect all sub-entity field keys to identify which fields belong to sub-entities
                const allSubEntityFieldKeys = new Set<string>();
                if (subEntityData) {
                  subEntityData.forEach((entity: any) => {
                    entity.subEntityFieldKeys.forEach((key: string) => allSubEntityFieldKeys.add(key));
                  });
                }

                return (
                  <React.Fragment key={`availability-fragment-${index}`}>
                    {/* Main availability row - only render if there are NO sub-entities */}
                    {!subEntityData && (
                      <TableRow className={classes.root} tabIndex={-1} key={row.id}>
                        {
                          isIncludeAvailabilityName && <TableCell
                              component="th"
                              className={`${classes.noBottomBorder} ${classes.stickyColumn}`}
                              key={`${index}-availabilityName-cell`}
                              style={{
                                verticalAlign: 'middle',
                                borderTop: index > 0 ? '2px solid #DFE6E5' : undefined,
                              }}
                            >
                              <Typography variant="body1">
                              {isCESurvey ? getAvailabilityNameValueLocal(row) :
                                getAvailabilityFieldValues(AVAILABILITY_NAME_KEY, row)}
                                      </Typography>

                            </TableCell>
                         }
                        {displayFields.map((column: any) => {
                          const value = getAvailabilityFieldValues(
                            column.fieldKey,
                            row,
                          )?.trim();
                          return (
                          <TableCell
                            className={classes.noBottomBorder}
                            component="th"
                            key={`${column.fieldKey}-cell`}
                            style={{
                              verticalAlign: 'middle',
                              borderTop: index > 0 ? '2px solid #DFE6E5' : undefined,
                            }}
                          >
                            {(() => {
                              const isLink =
                                ((surveyCountry_PAC && linkAttributes_pac.includes(column.fieldLabel)) ||
                                  (surveyCountry_UK && linkAttributes_UK_Regional.includes(column.fieldLabel)) ||
                                  (!surveyCountry_PAC && linkAttributes.includes(column.fieldLabel))) &&
                                !!value;
                              const isMeasurement =
                                Measurement.includes(column.fieldLabel) && surveyCountry_CE;
                              const isHTML = /<[a-z][\s\S]*>/i.test(value);

                              switch (true) {
                                case isLink:
                                  return renderLinks(value);
                                case isHTML: {
                                  const safeHtml = DOMPurify.sanitize(value);
                                  return (
                                    <Typography
                                      variant="body1"
                                      dangerouslySetInnerHTML={{ __html: safeHtml }}
                                      className={classes.contentStyle}
                                    />
                                  );
                                }
                                case isMeasurement:
                                  return renderFormattedMeasurementText(value || '-');
                                default:
                                  return (
                                  <Typography variant="body1">
                                  {value || '-'}
                                  </Typography>
                                  );
                              }
                            })()}
                          </TableCell>
                          );
                        })}
                        {emptyCells(`${index}-table-content`)}
                      </TableRow>
                    )}

                    {/* Render sub-entity rows directly in the main table */}
                    {subEntityData && subEntityData.map((entityData: any, entityIdx: number) => {
                      const {
                        data,
                        availability: parentAvailability,
                        subEntityFieldKeys,
                      } = entityData;

                      return data.map((dataRow: any, dataIdx: number) => {
                        // Calculate if this is the first row across all entities
                        const isFirstRowOfAllEntities = entityIdx === 0 && dataIdx === 0;
                        // Calculate total rows across all entities for rowSpan
                        const totalSubEntityRows = subEntityData.reduce(
                          (sum: number, entity: any) => sum + entity.data.length,
                          0,
                        );

                        return (
                          <TableRow
                            key={`sub-entity-${index}-${entityIdx}-${dataIdx}`}
                            style={{
                              backgroundColor: '#F9F9F9',
                            }}>
                            {/* Availability name cell - only on first sub-entity row, spans all sub-entity rows */}
                            {isIncludeAvailabilityName && isFirstRowOfAllEntities && (
                              <TableCell
                                rowSpan={totalSubEntityRows}
                                className={`${classes.noBottomBorder} ${classes.stickyColumn}`}
                                style={{
                                  verticalAlign: 'middle',
                                  borderTop: index > 0 ? '2px solid #DFE6E5' : undefined,
                                }}>
                                <Typography variant="body1">
                                  {isCESurvey
                                    ? getAvailabilityNameValueLocal(parentAvailability)
                                    : getAvailabilityFieldValues(
                                      AVAILABILITY_NAME_KEY,
                                      parentAvailability,
                                    )}
                                </Typography>
                              </TableCell>
                            )}

                            {/* Data cells for each field */}
                            {displayFields.map((column: any, fieldIndex: number) => {
                              const { fieldKey } = column;
                              const isSubEntityField = subEntityFieldKeys.has(fieldKey);

                              if (isSubEntityField) {
                              // Sub-entity field - show value from dataRow
                                const value = formatNumericValue(dataRow[fieldKey], fieldKey);

                                return (
                                <TableCell
                                  key={`sub-cell-${fieldIndex}`}
                                  className={classes.noBottomBorder}
                                  style={{
                                    fontSize: '16px',
                                    borderTop: isFirstRowOfAllEntities && index > 0 ? '2px solid #DFE6E5' : undefined,
                                  }}>
                                  <Typography variant="body1">
                                    {value}
                                  </Typography>
                                </TableCell>
                                );
                              }
                              if (dataIdx === 0) {
                              // Parent field - show merged cell on first row only
                                const value = getAvailabilityFieldValues(fieldKey, parentAvailability)?.trim() || '-';
                                const isLink =
                                ((surveyCountry_PAC && linkAttributes_pac.includes(column.fieldLabel)) ||
                                  (surveyCountry_UK && linkAttributes_UK_Regional.includes(column.fieldLabel)) ||
                                  (!surveyCountry_PAC && linkAttributes.includes(column.fieldLabel))) &&
                                !!value;
                                const isMeasurement =
                                Measurement.includes(column.fieldLabel) && surveyCountry_CE;
                                const isHTML = /<[a-z][\s\S]*>/i.test(value);

                                return (
                                <TableCell
                                  key={`parent-cell-merged-${fieldIndex}`}
                                  rowSpan={data.length}
                                  className={classes.noBottomBorder}
                                  style={{
                                    backgroundColor: '#FEFEFE',
                                    verticalAlign: 'middle',
                                    fontSize: '16px',
                                    borderTop: isFirstRowOfAllEntities && index > 0 ? '2px solid #DFE6E5' : undefined,
                                  }}>
                                  {(() => {
                                    switch (true) {
                                      case isLink:
                                        return renderLinks(value);
                                      case isMeasurement:
                                        return renderFormattedMeasurementText(value || '-');
                                      case isHTML:
                                        return (
                                          <Typography
                                            variant="body1"
                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
                                            className={classes.contentStyle}
                                          />
                                        );
                                      default:
                                        return (
                                        <Typography variant="body1">
                                        {value}
                                        </Typography>
                                        );
                                    }
                                  })()}
                                </TableCell>
                                );
                              }
                              // Parent field on subsequent rows - skip (already merged)
                              return null;
                            })}
                            {/* Add empty cells to match the table width */}
                            {emptyCells(`${index}-${entityIdx}-${dataIdx}-sub-entity`)}
                          </TableRow>
                        );
                      });
                    })}

                    {/* Add total rows if sub-entity data exists and showTotals is true */}
                    {showTotals && subEntityData && subEntityData.length > 0 && (() => {
                      // Use survey property type directly
                      const propertyType = survey?.propertyType?.trim();

                      // Calculate totals for property type and all
                      const propertyTypeTotals = calculateSubEntityTotals(
                        subEntityData,
                        true,
                        propertyType,
                      );
                      const allTotals = calculateSubEntityTotals(subEntityData, false);

                      // Get the first sub-entity to extract unit information
                      const firstEntity = subEntityData[0];
                      const { subEntityFieldKeys, subEntityFields, allColumns } = firstEntity;

                      // Find the space type field in template columns
                      const spaceTypeFieldDef = (allColumns || subEntityFields).find((field: any) => {
                        const fieldKey = (field.fieldKey || field.field || '').toLowerCase();
                        return fieldKey.includes('space_type') || fieldKey.includes('spacetype');
                      });
                      const spaceTypeFieldKey = spaceTypeFieldDef?.fieldKey || spaceTypeFieldDef?.field;

                      // Check if space type field is in displayFields (visible columns) - same logic as column visibility
                      const isSpaceTypeActive = displayFields.some((field: any) =>
                        (field.fieldKey || field.field) === spaceTypeFieldKey,
                      );

                      // Helper function to render a total row
                      const renderTotalRow = (
                        totals: { [key: string]: number },
                        spaceTypeLabel: string,
                        rowKey: string,
                        isLast: boolean,
                      ) => (
                        <TableRow
                          key={rowKey}
                          style={{
                            backgroundColor: '#F5F7F7',
                            fontWeight: 600,
                            borderTop: '2px solid #DFE6E5',
                            borderBottom: isLast ? '2px solid #DFE6E5' : undefined,
                          }}>
                          {/* Availability name column - show empty */}
                          {isIncludeAvailabilityName && (
                            <TableCell
                              className={`${classes.noBottomBorder} ${classes.stickyColumn}`}
                              style={{
                                fontWeight: 600,
                                fontSize: '16px',
                              }}>
                              <Typography
                                variant="body1"
                                style={{ fontWeight: 600 }}>
                                {/* Empty - no label in availability name column */}
                              </Typography>
                            </TableCell>
                          )}

                          {/* Data cells for each field */}
                          {displayFields.map((column: any, fieldIndex: number) => {
                            const { fieldKey } = column;
                            const isSubEntityField = subEntityFieldKeys.has(fieldKey);
                            const totalValue = totals[fieldKey];

                            if (isSubEntityField) {
                              // For the first sub-entity field, show "Total" label
                              if (fieldIndex === 0) {
                                return (
                                  <TableCell
                                    key={`${rowKey}-cell-${fieldIndex}`}
                                    className={classes.noBottomBorder}
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '16px',
                                    }}>
                                    <Typography
                                      variant="body1"
                                      style={{ fontWeight: 600 }}>
                                      {t('availabilities.total')}
                                    </Typography>
                                  </TableCell>
                                );
                              }

                              // For space type field, show the space type label
                              if (spaceTypeFieldKey && fieldKey === spaceTypeFieldKey) {
                                return (
                                  <TableCell
                                    key={`${rowKey}-cell-${fieldIndex}`}
                                    className={classes.noBottomBorder}
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '16px',
                                    }}>
                                    <Typography
                                      variant="body1"
                                      style={{ fontWeight: 600 }}>
                                      {spaceTypeLabel}
                                    </Typography>
                                  </TableCell>
                                );
                              }

                              // For numeric fields with totals, show the total value
                              if (totalValue !== undefined) {
                                // Extract unit from the field (look at first data row)
                                const firstDataRow = firstEntity.data[0];
                                const firstValue = firstDataRow?.[fieldKey] || '';
                                const unit = extractUnit(firstValue);

                                // Format number with comma separators
                                // Only show decimals if the value is not an integer
                                const isInteger = Number.isInteger(totalValue);
                                const formattedValue = totalValue.toLocaleString('en-US', {
                                  minimumFractionDigits: isInteger ? 0 : 2,
                                  maximumFractionDigits: 2,
                                });

                                return (
                                  <TableCell
                                    key={`${rowKey}-cell-${fieldIndex}`}
                                    className={classes.noBottomBorder}
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '16px',
                                    }}>
                                    <Typography
                                      variant="body1"
                                      style={{ fontWeight: 600 }}>
                                      {`${formattedValue} ${unit}`.trim()}
                                    </Typography>
                                  </TableCell>
                                );
                              }

                              // For non-numeric sub-entity fields, show empty cell
                              return (
                                <TableCell
                                  key={`${rowKey}-cell-${fieldIndex}`}
                                  className={classes.noBottomBorder}
                                  style={{
                                    fontSize: '16px',
                                  }}>
                                  <Typography variant="body1">
                                    -
                                  </Typography>
                                </TableCell>
                              );
                            }

                            // For parent fields, show empty cell (no dash)
                            return (
                              <TableCell
                                key={`${rowKey}-cell-${fieldIndex}`}
                                className={classes.noBottomBorder}
                                style={{
                                  backgroundColor: '#FEFEFE',
                                  fontSize: '16px',
                                }}>
                                <Typography variant="body1">
                                  {/* Empty - no content */}
                                </Typography>
                              </TableCell>
                            );
                          })}
                          {/* Add empty cells to match the table width */}
                          {emptyCells(`${rowKey}-totals`)}
                        </TableRow>
                      );

                      return (
                        <>
                          {/* Property type total row - only show if space type field is active */}
                          {propertyType && isSpaceTypeActive && renderTotalRow(
                            propertyTypeTotals,
                            propertyType,
                            `total-property-type-${index}`,
                            false,
                          )}
                          {/* All space types total row */}
                          {renderTotalRow(
                            allTotals,
                            t('availabilities.all'),
                            `total-all-${index}`,
                            true,
                          )}
                        </>
                      );
                    })()}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  ) : null;
};

export default PropertyAvailabilities;
